import { cloneElement, forwardRef, isValidElement, useEffect, useRef, useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Instance, Options, Placement, createPopper } from '@popperjs/core';
/// TODO: Popper will be replaced when the own tooltip is implemented and may use positioning from utils
import { createPortal } from 'react-dom';
import classNames from '../../utils/classnames';
import { IOverlayInjectedProps, IOverlayProps } from './types';

const FALLBACK_PLACEMENTS = ['top', 'right', 'bottom', 'left'] as const;
const OFFSET = [0, 8] as const;
const VIEWPORT_PADDING = 16;

/**
 * Overlay component that matches react-bootstrap's Overlay behavior
 * Uses Popper.js for positioning just like react-bootstrap
 */
const Overlay = forwardRef<HTMLElement, IOverlayProps>((props, ref) => {
    const {
        show = false,
        target,
        children,
        placement = 'top',
        container,
        rootClose = false,
        rootCloseEvent = 'click',
        onHide,
        flip = true,
        fallbackPlacements = FALLBACK_PLACEMENTS,
        onMouseEnter,
        onMouseLeave,
        ...propsRest
    } = props;

    const overlayRef = useRef<HTMLElement | null>(null);
    const popperInstance = useRef<Instance | null>(null);
    const [arrowElement, setArrowElement] = useState<HTMLElement | null>(null);
    const [hasDoneInitialMeasure, setHasDoneInitialMeasure] = useState(false);
    const [currentPlacement, setCurrentPlacement] = useState<Placement>(placement as Placement);

    // Merge refs
    const mergeRefs = (el: HTMLElement | null) => {
        overlayRef.current = el;
        if (typeof ref === 'function') {
            ref(el);
        } else if (ref) {
            ref.current = el;
        }
    };

    // Create and update Popper instance
    useEffect(() => {
        if (!show || !target || !overlayRef.current) {
            if (popperInstance.current) {
                popperInstance.current.destroy();
                popperInstance.current = null;
            }

            setHasDoneInitialMeasure(false);

            return;
        }

        // Create Popper instance
        const modifiers: Options['modifiers'] = [
            {
                name: 'offset',
                options: {
                    offset: OFFSET
                }
            },
            {
                name: 'arrow',
                options: {
                    element: arrowElement
                }
            },
            {
                name: 'preventOverflow',
                options: {
                    boundary: 'clippingParents',
                    rootBoundary: 'viewport',
                    padding: VIEWPORT_PADDING
                }
            }
        ];

        if (flip) {
            modifiers.push({
                name: 'flip',
                options: {
                    fallbackPlacements
                }
            });
        }

        modifiers.push({
            name: 'reactPlacementSync',
            enabled: true,
            phase: 'afterWrite',
            fn: ({ state }) => {
                setCurrentPlacement(state.placement as Placement);
            }
        });

        popperInstance.current = createPopper(target, overlayRef.current, {
            placement: placement as Placement,
            modifiers
        });

        // Initial measurement
        popperInstance.current?.forceUpdate?.();
        setHasDoneInitialMeasure(true);

        return () => {
            if (popperInstance.current) {
                popperInstance.current.destroy();
                popperInstance.current = null;
            }
        };
    }, [show, target, placement, arrowElement, flip, fallbackPlacements]);

    useEffect(() => {
        setCurrentPlacement(placement as Placement);
    }, [placement]);

    // Update popper on show/hide
    useEffect(() => {
        if (popperInstance.current && show) {
            popperInstance.current.forceUpdate?.();
        }
    }, [show]);

    useEffect(() => {
        if (!show) {
            return;
        }

        const handleOrientationChange = () => {
            onHide?.();
        };

        window.addEventListener('orientationchange', handleOrientationChange);

        return () => {
            window.removeEventListener('orientationchange', handleOrientationChange);
        };
    }, [show, onHide]);

    // Handle root close
    useEffect(() => {
        if (!show || !rootClose) {
            return;
        }

        const handleRootClose = (event: Event) => {
            if (
                overlayRef.current &&
                !overlayRef.current.contains(event.target as Node) &&
                target &&
                !target.contains(event.target as Node)
            ) {
                onHide?.();
            }
        };

        const eventType = rootCloseEvent === 'click' ? 'mousedown' : rootCloseEvent;

        document.addEventListener(eventType, handleRootClose);

        return () => {
            document.removeEventListener(eventType, handleRootClose);
        };
    }, [show, rootClose, rootCloseEvent, target, onHide]);

    if (!show) {
        return null;
    }

    const popper = popperInstance.current;
    const childElement =
        typeof children !== 'function' ? (children as React.ReactElement<IOverlayInjectedProps>) : null;

    // Props to inject into children
    const injectedProps: IOverlayInjectedProps = {
        ref: mergeRefs,
        style: {},
        'aria-labelledby': propsRest['aria-labelledby'],
        arrowProps: {
            ref: setArrowElement,
            style: {}
        },
        show,
        className: classNames(childElement?.props.className, propsRest.className, show && 'show'),
        placement: currentPlacement,
        popper: {
            state: popper?.state,
            outOfBoundaries: false,
            placement: currentPlacement,
            scheduleUpdate: () => popper?.update?.()
        },
        hasDoneInitialMeasure,
        ...propsRest
    };

    // Render children with injected props
    const renderChildren = () => {
        if (typeof children === 'function') {
            return children(injectedProps);
        }

        if (isValidElement(children)) {
            return cloneElement(children, injectedProps);
        }

        return null;
    };

    const overlayElement = renderChildren();

    if (!overlayElement) {
        return null;
    }

    // Wrap with mouse handlers if provided (for hover trigger support)
    if (onMouseEnter || onMouseLeave) {
        return createPortal(
            <div onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
                {overlayElement}
            </div>,
            container || document.body
        );
    }

    return createPortal(overlayElement, container || document.body);
});

Overlay.displayName = 'Overlay';

export default Overlay;
